import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, AlertCircle, CheckCircle2, ShieldCheck } from 'lucide-react';

function checkPolicy(password) {
  const rules = {
    length: password.length >= 10,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    digit: /[0-9]/.test(password),
    symbol: /[^A-Za-z0-9]/.test(password),
  };
  const passed = Object.values(rules).every(Boolean);
  return { rules, passed };
}

const DispatchResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [errorMsg, setErrorMsg] = useState('');

  const { rules, passed } = useMemo(() => checkPolicy(password), [password]);
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = passed && !mismatch && confirm.length > 0 && token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Unable to reset password.');
      }
      setStatus('done');
      setTimeout(() => navigate('/dispatch/login', { replace: true }), 2200);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#004C97] flex items-center justify-center px-4">
        <Card className="max-w-md w-full" data-testid="dispatch-reset-no-token">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
            <p className="text-lg font-semibold text-gray-900">Reset link is missing a token.</p>
            <p className="text-sm text-gray-600 mt-1">
              Request a new reset link and try again.
            </p>
            <Link to="/dispatch/forgot-password" className="mt-4 inline-block text-[#004C97] font-medium hover:underline">
              Request a new link
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#004C97] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">MoveraCare Mobility</h1>
          <p className="text-[#F9A825] text-lg font-semibold">Set a new password</p>
        </div>

        <Card className="border-2 border-[#F9A825]/20 shadow-2xl" data-testid="dispatch-reset-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center text-[#004C97]">Create your password</CardTitle>
            <CardDescription className="text-center">
              Choose a strong password to secure the dispatch portal.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {status === 'done' ? (
              <div
                className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start space-x-3"
                data-testid="dispatch-reset-success"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-emerald-900 font-medium">Password updated.</p>
                  <p className="text-xs text-emerald-800 mt-1">Redirecting you to sign in…</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="dispatch-reset-form">
                {status === 'error' && (
                  <div
                    className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2"
                    data-testid="dispatch-reset-error"
                  >
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{errorMsg}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700" htmlFor="dispatch-reset-password">
                    New password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="dispatch-reset-password"
                      data-testid="dispatch-reset-password"
                      type="password"
                      required
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700" htmlFor="dispatch-reset-confirm">
                    Confirm password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="dispatch-reset-confirm"
                      data-testid="dispatch-reset-confirm"
                      type="password"
                      required
                      autoComplete="new-password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F9A825] focus:border-transparent ${
                        mismatch ? 'border-red-400' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {mismatch && (
                    <p className="text-xs text-red-600" data-testid="dispatch-reset-mismatch">
                      Passwords do not match.
                    </p>
                  )}
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1 text-xs" data-testid="dispatch-reset-rules">
                  <p className="font-semibold text-slate-700 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5" /> Password must include:
                  </p>
                  <ul className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-slate-600">
                    <li className={rules.length ? 'text-emerald-700' : ''}>• 10+ characters</li>
                    <li className={rules.upper ? 'text-emerald-700' : ''}>• Uppercase letter</li>
                    <li className={rules.lower ? 'text-emerald-700' : ''}>• Lowercase letter</li>
                    <li className={rules.digit ? 'text-emerald-700' : ''}>• Number</li>
                    <li className={rules.symbol ? 'text-emerald-700' : ''}>• Symbol</li>
                  </ul>
                </div>

                <Button
                  type="submit"
                  disabled={!canSubmit || status === 'loading'}
                  data-testid="dispatch-reset-submit"
                  className="w-full bg-[#F9A825] hover:bg-[#E08E1E] text-white font-semibold py-2 disabled:opacity-60"
                >
                  {status === 'loading' ? 'Updating…' : 'Update password'}
                </Button>
              </form>
            )}

            <div className="mt-6 pt-6 border-t border-gray-200 flex justify-center">
              <Link
                to="/dispatch/login"
                className="text-sm text-[#004C97] hover:underline font-medium"
                data-testid="dispatch-reset-back-to-login"
              >
                Back to sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DispatchResetPassword;
